import React, { useState } from "react";
import { ArrowLeft, Sparkles, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { jobCampaignsService } from "../../../Services/jobCampaigns.service";
import ExampleBrief from "../../../Components/ExampleBrief";
import { useSidebar } from "../../../Components/ui/sidebarNew";

export default function ProjectDescription({
  setTab,
  setToastList,
  toastList,
  onBriefGenerated,
}) {
  const [description, setDescription] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showExampleModal, setShowExampleModal] = useState(false);
  const navigate = useNavigate();
  const { state } = useSidebar();

  const handleGenerateBrief = async () => {
    const trimmedDescription = description.trim();

    if (!trimmedDescription) {
      setError("Please enter a project description");
      return;
    }

    if (trimmedDescription.length < 150) {
      setError("Description must be at least 150 characters long");
      return;
    }

    if (trimmedDescription.length > 5000) {
      setError("Description must not exceed 5000 characters");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await jobCampaignsService.generateCampaignWithAI(
        description
      );
      console.log("Brief generated successfully:", response);

      const briefData = {
        ...response.data,
        source: "ai-generated",
      };

      navigate("/brand/jobpostbrief", {
        state: { briefData, fromDescription: true, isEditMode: false },
      });

      if (onBriefGenerated) {
        onBriefGenerated(briefData);
      }
    } catch (err) {
      console.error("Error generating brief:", err);
      setError("Failed to generate brief. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUseExample = (exampleText) => {
    setDescription(exampleText);
    setError(null);
  };

  return (
    <>
      <div
        style={{
          marginLeft: state === "collapsed" ? "28px" : "",
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 100px)",
          overflow: "hidden",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
          {/* Header Section */}
          <header 
            className="flex pb-4 flex-col md:flex-row md:items-center md:justify-between gap-3" 
            style={{ flexShrink: 0, zIndex: 20, position: "relative" }}
          >
            {/* Left Side - Back Button and Title */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/brand/jobpostmain")}
                className="flex h-[38px] px-5 justify-center items-center rounded-full text-sm font-medium cursor-pointer transition-all duration-200 bg-gray-50 text-gray-600 hover:bg-green-50 hover:text-green-700 border-none"
                style={{
                  boxShadow: "0px 1px 3px 0px rgba(16, 24, 40, 0.08)",
                }}
              >
                <ArrowLeft strokeWidth={2.2} size={18} className="mr-2" />
                Back
              </button>
              <h2 className="text-xl font-bold text-gray-900">
                Describe Your Project
              </h2>
            </div>
          </header>

          {/* Main Content Container */}
          <div
            className="rounded-xl bg-white overflow-hidden w-full"
            style={{
              boxShadow: "0px 1px 3px 0px rgba(16, 24, 40, 0.08)",
              border: "1px solid rgba(16, 185, 129, 0.1)",
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
            }}
          >
            <div
              className="p-6"
              style={{
                flex: 1,
                overflowY: "auto",
                scrollbarWidth: "thin",
                scrollbarColor: "#e5e7eb transparent",
              }}
            >
              {/* Instruction Text */}
              <p className="text-gray-600 mb-4 text-sm">
                Describe your campaign project in detail. Our AI will help you generate a professional campaign brief.
              </p>

              {/* Textarea */}
              <div className="mb-6">
                <textarea
                  rows={12}
                  placeholder="Enter your project description here... Include details about your brand, target audience, campaign goals, preferred influencer type, and any specific requirements."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full p-4 border rounded-xl text-gray-700 focus:outline-none focus:ring-1 focus:ring-green-400 focus:border-green-400 resize-none transition-all duration-200"
                  style={{
                    borderColor: "#E5E7EB",
                    background: "#FAFAFA",
                    minHeight: "280px",
                  }}
                />
                
                {/* Character Count */}
                <div className="flex justify-between items-center mt-3">
                  <div className="text-sm text-gray-500">
                    {description.length}/5000 characters
                    {description.length < 150 && (
                      <span className="text-orange-500 ml-2">
                        (Minimum 150 characters required)
                      </span>
                    )}
                  </div>
                  <div className="text-sm">
                    {description.length >= 150 && description.length <= 5000 ? (
                      <span className="text-green-600 font-medium">✓ Valid length</span>
                    ) : description.length > 5000 ? (
                      <span className="text-red-600 font-medium">Too long</span>
                    ) : (
                      <span className="text-gray-400">
                        {150 - description.length} more characters needed
                      </span>
                    )}
                  </div>
                </div>
                
                {error && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                    {error}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row justify-end items-center gap-4 pt-4 border-t border-gray-100">
                <button
                  onClick={() => setShowExampleModal(true)}
                  className="flex h-[42px] px-6 items-center gap-2 rounded-full text-sm font-medium cursor-pointer transition-all duration-200 hover:bg-green-50"
                  style={{
                    background: "transparent",
                    border: "2px solid #10B981",
                    color: "#10B981",
                    boxShadow: "0px 1px 3px 0px rgba(16, 24, 40, 0.08)",
                  }}
                >
                  <FileText strokeWidth={2.2} size={16} />
                  Example Campaigns
                </button>
                
                <button
                  onClick={handleGenerateBrief}
                  disabled={
                    isLoading ||
                    description.trim().length < 150 ||
                    description.trim().length > 5000
                  }
                  className="flex h-[42px] px-6 items-center gap-2 rounded-full text-sm font-semibold cursor-pointer border-none text-white transition-all duration-200 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                    boxShadow: "0px 2px 6px 0px rgba(16, 185, 129, 0.25)",
                  }}
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles strokeWidth={2.2} size={16} />
                      Generate Campaign
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ExampleBrief
        isOpen={showExampleModal}
        onClose={() => setShowExampleModal(false)}
        onUseExample={handleUseExample}
      />
    </>
  );
}

